import { useState } from 'react';
import type { GlobalTag, List, ListTag, Selection, View } from '../db/types';
import { colorOf } from '../lib/colors';
import { thisMonthKey } from '../lib/dates';
import type { Counts } from '../lib/views';
import './Sidebar.css';

export interface SidebarProps {
  view: View;
  lists: List[];
  listTags: ListTag[];
  globalTags: GlobalTag[];
  counts: Counts;
  status: string;
  statusDetail?: string | null;
  /** Which list is expanded. Only ever one. */
  expandedListId: string | null;
  onExpandList: (listId: string | null) => void;
  onSelect: (selection: Selection) => void;
  /** Ctrl/Cmd-click on a global tag stacks it as a filter instead of selecting. */
  onStackGlobalTag: (tagId: string) => void;
  onSync: () => void;
  onNewList: () => void;
  onEditList: (list: List) => void;
  onDeleteList: (list: List) => void;
  onReorderList: (id: string, index: number) => void;
  onNewListTag: (listId: string) => void;
  onEditListTag: (tag: ListTag) => void;
  onDeleteListTag: (tag: ListTag) => void;
  onNewGlobalTag: () => void;
  onEditGlobalTag: (tag: GlobalTag) => void;
  onDeleteGlobalTag: (tag: GlobalTag) => void;
  onReorderGlobalTag: (id: string, index: number) => void;
}

const NAV = [
  { type: 'today', label: 'Today' },
  { type: 'upcoming', label: 'Upcoming' },
  { type: 'calendar', label: 'Calendar' },
  { type: 'inbox', label: 'Inbox' },
  { type: 'all', label: 'All Notes' },
] as const;

/** The calendar always opens on the current month; the rest carry no state. */
const navSelection = (type: (typeof NAV)[number]['type']): Selection =>
  type === 'calendar' ? { type, month: thisMonthKey() } : { type };

/**
 * Four sections: where you are going, where notes live, how they are
 * categorised across everything, and the machinery.
 *
 * Lists expand one at a time. A list's own tags belong under it and nowhere
 * else, so the tree never grows past two levels — which is what keeps a
 * sidebar with fifty tags in it still readable.
 */
export function Sidebar(props: SidebarProps) {
  const {
    view,
    lists,
    listTags,
    globalTags,
    counts,
    status,
    statusDetail,
    expandedListId,
    onSelect,
    onStackGlobalTag,
    onSync,
    onNewList,
    onNewGlobalTag,
  } = props;

  const [dragging, setDragging] = useState<string | null>(null);
  const selection = view.selection;

  return (
    <nav className="nav" aria-label="Navigation">
      <div className="nav__head">
        <span className="nav__brand">SlippingNotes</span>
        <button
          type="button"
          className={`kbd nav__status${statusDetail ? ' is-failed' : ''}`}
          title={statusDetail ?? 'Sync now'}
          onClick={onSync}
        >
          {status}
        </button>
      </div>

      {/* A failure that only exists in a tooltip is a failure you never read.
          The server's own words are what makes it fixable. */}
      {statusDetail && <p className="nav__error">{statusDetail}</p>}

      {/* ── 1 · Navigation ─────────────────────────────── */}
      <div className="nav__group">
        {NAV.map((entry) => (
          <NavRow
            key={entry.type}
            label={entry.label}
            count={counts[entry.type]}
            active={selection.type === entry.type}
            onClick={() => onSelect(navSelection(entry.type))}
          />
        ))}
      </div>

      {/* ── 2 · Lists ──────────────────────────────────── */}
      <div className="nav__group">
        <SectionLabel label="LISTS" onAdd={onNewList} addTitle="New list" />
        {lists.map((list, index) => (
          <ListRow
            key={list.id}
            {...props}
            list={list}
            index={index}
            tags={listTags.filter((t) => t.list_id === list.id)}
            selection={selection}
            expanded={expandedListId === list.id}
            dragging={dragging}
            setDragging={setDragging}
          />
        ))}
      </div>

      {/* ── 3 · Global tags ────────────────────────────── */}
      <div className="nav__group">
        <SectionLabel label="GLOBAL TAGS" onAdd={onNewGlobalTag} addTitle="New global tag" />
        {globalTags.length === 0 && <div className="nav__none">None yet</div>}
        {globalTags.map((tag, index) => {
          const active = selection.type === 'globalTag' && selection.tagId === tag.id;
          const stacked = view.globalTagIds.includes(tag.id);
          return (
            <div
              key={tag.id}
              className={`nav__item${active ? ' is-active' : ''}${stacked ? ' is-stacked' : ''}`}
              role="button"
              tabIndex={0}
              draggable
              title={stacked ? 'Filtering by this tag' : 'Click to open · ⌘-click to add as filter'}
              onDragStart={() => setDragging(tag.id)}
              onDragEnd={() => setDragging(null)}
              onDragOver={(e) => dragging && e.preventDefault()}
              onDrop={() => {
                if (dragging && dragging !== tag.id) props.onReorderGlobalTag(dragging, index);
                setDragging(null);
              }}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey) onStackGlobalTag(tag.id);
                else onSelect({ type: 'globalTag', tagId: tag.id });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect({ type: 'globalTag', tagId: tag.id });
                }
              }}
            >
              <span
                className="nav__dot"
                style={colorOf(tag.color) && !active ? { background: colorOf(tag.color)! } : undefined}
                aria-hidden="true"
              />
              <span className="nav__name">{tag.name}</span>
              <RowActions
                onEdit={() => props.onEditGlobalTag(tag)}
                onDelete={() => props.onDeleteGlobalTag(tag)}
              />
              <span className="nav__count">{counts.globalTags[tag.id] ?? 0}</span>
            </div>
          );
        })}
      </div>

      <div className="spacer" />

      {/* ── 4 · System ─────────────────────────────────── */}
      <div className="nav__group nav__group--system">
        <button
          type="button"
          className={`nav__system${selection.type === 'trash' ? ' is-active' : ''}`}
          onClick={() => onSelect({ type: 'trash' })}
        >
          Trash
          {counts.trash > 0 && <span className="nav__count">{counts.trash}</span>}
        </button>
        <button
          type="button"
          className={`nav__system${selection.type === 'conflicts' ? ' is-active' : ''}`}
          onClick={() => onSelect({ type: 'conflicts' })}
        >
          Conflicts
        </button>
        <button
          type="button"
          className={`nav__system${selection.type === 'settings' ? ' is-active' : ''}`}
          onClick={() => onSelect({ type: 'settings' })}
        >
          Settings
        </button>
        <span className="spacer" />
        <span className="kbd">⌘K</span>
      </div>
    </nav>
  );
}

/* ── Pieces ──────────────────────────────────────────────── */

function SectionLabel({
  label,
  onAdd,
  addTitle,
}: {
  label: string;
  onAdd: () => void;
  addTitle: string;
}) {
  return (
    <div className="nav__label">
      <span className="spacer">{label}</span>
      <button type="button" className="nav__labelAdd" title={addTitle} aria-label={addTitle} onClick={onAdd}>
        +
      </button>
    </div>
  );
}

function NavRow({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`nav__item${active ? ' is-active' : ''}`}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
    >
      <span className="nav__dot" aria-hidden="true" />
      <span className="nav__name">{label}</span>
      <span className="nav__count">{count}</span>
    </button>
  );
}

interface ListRowProps extends SidebarProps {
  list: List;
  index: number;
  tags: ListTag[];
  expanded: boolean;
  selection: Selection;
  dragging: string | null;
  setDragging: (id: string | null) => void;
}

function ListRow({
  list,
  index,
  tags,
  counts,
  selection,
  expanded,
  dragging,
  setDragging,
  onExpandList,
  onSelect,
  onEditList,
  onDeleteList,
  onReorderList,
  onNewListTag,
  onEditListTag,
  onDeleteListTag,
}: ListRowProps) {
  const active = selection.type === 'list' && selection.listId === list.id;
  const hex = colorOf(list.color);

  return (
    <>
      <div
        className={`nav__item${active ? ' is-active' : ''}`}
        role="treeitem"
        tabIndex={0}
        aria-expanded={expanded}
        draggable={!list.system}
        onDragStart={() => !list.system && setDragging(list.id)}
        onDragEnd={() => setDragging(null)}
        onDragOver={(e) => dragging && e.preventDefault()}
        onDrop={() => {
          if (dragging && dragging !== list.id) onReorderList(dragging, index);
          setDragging(null);
        }}
        onClick={() => {
          // Selecting a list also opens it — the tags belong to what you just
          // chose, so revealing them is the same gesture, not a second one.
          onExpandList(expanded && active ? null : list.id);
          onSelect({ type: 'list', listId: list.id });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect({ type: 'list', listId: list.id });
            onExpandList(list.id);
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            onExpandList(list.id);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            onExpandList(null);
          }
        }}
      >
        <span
          className="nav__twisty"
          aria-hidden="true"
          onClick={(e) => {
            e.stopPropagation();
            onExpandList(expanded ? null : list.id);
          }}
        >
          {expanded ? '▾' : '▸'}
        </span>
        <span
          className="nav__dot"
          style={hex && !active ? { background: hex } : undefined}
          aria-hidden="true"
        />
        <span className="nav__name">{list.name}</span>
        {!list.system && (
          <RowActions onEdit={() => onEditList(list)} onDelete={() => onDeleteList(list)} />
        )}
        <span className="nav__count">{counts.lists[list.id] ?? 0}</span>
      </div>

      {expanded && (
        <div className="nav__children">
          <button
            type="button"
            className={`nav__item nav__item--child${active ? ' is-active' : ''}`}
            onClick={() => onSelect({ type: 'list', listId: list.id })}
          >
            <span className="nav__name">All Notes</span>
            <span className="nav__count">{counts.lists[list.id] ?? 0}</span>
          </button>

          {tags.map((tag) => {
            const on = selection.type === 'listTag' && selection.tagId === tag.id;
            const tagHex = colorOf(tag.color);
            return (
              <div
                key={tag.id}
                className={`nav__item nav__item--child${on ? ' is-active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => onSelect({ type: 'listTag', listId: list.id, tagId: tag.id })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect({ type: 'listTag', listId: list.id, tagId: tag.id });
                  }
                }}
              >
                <span
                  className="nav__tagDot"
                  style={{ background: tagHex ?? 'var(--border-default)' }}
                  aria-hidden="true"
                />
                <span className="nav__name">{tag.name}</span>
                <RowActions
                  onEdit={() => onEditListTag(tag)}
                  onDelete={() => onDeleteListTag(tag)}
                />
                <span className="nav__count">{counts.listTags[tag.id] ?? 0}</span>
              </div>
            );
          })}

          <button
            type="button"
            className="nav__item nav__item--child nav__item--add"
            onClick={() => onNewListTag(list.id)}
          >
            <span className="nav__name">+ New tag in this list</span>
          </button>
        </div>
      )}
    </>
  );
}

/** Edit and delete, revealed on hover so the row stays quiet at rest. */
function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <span className="nav__actions">
      <button
        type="button"
        className="nav__action"
        title="Rename"
        aria-label="Rename"
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
      >
        ✎
      </button>
      <button
        type="button"
        className="nav__action"
        title="Delete"
        aria-label="Delete"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        ⌫
      </button>
    </span>
  );
}
